declare global {
  interface Window {
    google: {
      maps: {
        places: {
          AutocompleteService: new () => google.maps.places.AutocompleteService;
          PlacesService: new (div: HTMLDivElement) => google.maps.places.PlacesService;
        };
        LatLng: new (lat: number, lng: number) => google.maps.LatLng;
        LatLngBounds: new (
          sw: google.maps.LatLng | google.maps.LatLngLiteral,
          ne: google.maps.LatLng | google.maps.LatLngLiteral
        ) => google.maps.LatLngBounds;
      };
    };
  }
}

export interface PlaceDetails {
  street: string;
  number: string;
}

export class GooglePlacesService {
  private static instance: GooglePlacesService;
  private autocompleteService?: google.maps.places.AutocompleteService;
  private placesService?: google.maps.places.PlacesService;

  // Romania bounds for location bias
  private readonly ROMANIA_RECTANGLE = {
    north: 48.2652,
    south: 43.6186,
    west: 20.2619,
    east: 29.7728
  };

  private constructor() {
    // Create a dummy div for PlacesService (required by Google Maps API)
    const dummyDiv = document.createElement('div');
    this.initServices(dummyDiv);
  }

  static getInstance(): GooglePlacesService {
    if (!this.instance) {
      this.instance = new GooglePlacesService();
    }
    return this.instance;
  }

  private initServices(dummyDiv: HTMLDivElement) {
    if (typeof window !== 'undefined' && window.google?.maps?.places) {
      this.autocompleteService = new window.google.maps.places.AutocompleteService();
      this.placesService = new window.google.maps.places.PlacesService(dummyDiv);
    }
  }

  async getPlacePredictions(input: string): Promise<google.maps.places.AutocompletePrediction[]> {
    if (!this.autocompleteService) {
      throw new Error('Google Places API not loaded');
    }

    try {
      const rectangle = {
        south: this.ROMANIA_RECTANGLE.south,
        west: this.ROMANIA_RECTANGLE.west,
        north: this.ROMANIA_RECTANGLE.north,
        east: this.ROMANIA_RECTANGLE.east
      };

      const request: google.maps.places.AutocompletionRequest = {
        input,
        componentRestrictions: { country: 'ro' },
        types: ['address'],
        locationRestriction: rectangle
      };

      const response = await new Promise<google.maps.places.AutocompletePrediction[]>((resolve, reject) => {
        this.autocompleteService!.getPlacePredictions(
          request,
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              resolve(predictions);
            } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              resolve([]);
            } else {
              reject(new Error(`Places API error: ${status}`));
            }
          }
        );
      });

      return response;
    } catch (error) {
      console.error('Error fetching place predictions:', error);
      return [];
    }
  }

  async getPlaceDetails(placeId: string): Promise<PlaceDetails> {
    if (!this.placesService) {
      throw new Error('Google Places API not loaded');
    }

    try {
      const request: google.maps.places.PlaceDetailsRequest = {
        placeId,
        fields: ['address_components']
      };

      const response = await new Promise<google.maps.places.PlaceResult>((resolve, reject) => {
        this.placesService!.getDetails(
          request,
          (result, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && result) {
              resolve(result);
            } else {
              reject(new Error(`Places API error: ${status}`));
            }
          }
        );
      });

      const streetNumber = response.address_components?.find(
        component => component.types.includes('street_number')
      )?.long_name || '';

      const street = response.address_components?.find(
        component => component.types.includes('route')
      )?.long_name || '';

      return {
        street,
        number: streetNumber
      };
    } catch (error) {
      console.error('Error fetching place details:', error);
      return { street: '', number: '' };
    }
  }
}

export const googlePlacesService = GooglePlacesService.getInstance();
